import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { GPA_LOGO } from "@/lib/assets";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const [state, setState] = useState("loading"); // loading | ok | error
  const [message, setMessage] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const token = params.get("token");
    if (!token) { setState("error"); setMessage("This verification link is missing its token."); return; }
    api.post("/registration/verify-email", { token })
      .then(({ data }) => { setState("ok"); setMessage(data.message); })
      .catch((err) => { setState("error"); setMessage(formatApiError(err.response?.data?.detail) || err.message); });
  }, [params]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
        <div className="mb-4 flex items-center justify-center rounded-lg bg-[#003057] px-3 py-2 mx-auto w-fit">
          <img src={GPA_LOGO} alt="GPA+" className="h-8 w-auto" />
        </div>
        {state === "loading" && (
          <div className="py-6" data-testid="verify-loading">
            <Loader2 className="mx-auto h-8 w-8 animate-spin text-[#003057]" />
            <p className="mt-3 text-sm text-slate-500">Verifying your email…</p>
          </div>
        )}
        {state === "ok" && (
          <div data-testid="verify-success">
            <CheckCircle2 className="mx-auto h-12 w-12 text-[#5fa114]" />
            <h1 className="mt-3 font-heading text-2xl font-extrabold text-[#003057]">Email verified</h1>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">{message}</p>
          </div>
        )}
        {state === "error" && (
          <div data-testid="verify-error">
            <XCircle className="mx-auto h-12 w-12 text-red-500" />
            <h1 className="mt-3 font-heading text-2xl font-extrabold text-[#003057]">Verification failed</h1>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">{message}</p>
          </div>
        )}
        <Link to="/login">
          <Button className="mt-6 w-full rounded-full bg-[#003057] py-6 text-base font-bold hover:bg-[#004275]" data-testid="verify-to-login">
            Go to sign in
          </Button>
        </Link>
      </div>
    </div>
  );
}
